import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import EventsPage from './EventsPage';
import MembersPage from './MembersPage';
import EditEventModal from '../components/EditEventModal';
import DeleteEventModal from '../components/DeleteEventModal';
import EditMemberModal from '../components/EditMemberModal';
import DeleteMemberModal from '../components/DeleteMemberModal';
import { API_BASE_URL } from '../constants/api';
import './DashboardPage.css';

type Page = 'dashboard' | 'members' | 'events';

// Helper function to safely render HTML content
const renderHtmlContent = (content: string) => {
  if (!content) return 'No description';
  return <div dangerouslySetInnerHTML={{ __html: content }} />;
};

const DashboardPage: React.FC = () => {
  const { user, logout } = useAppContext();
  const navigate = useNavigate();

  const [memberSearch, setMemberSearch] = useState('');
  const [selectedPage, setSelectedPage] = useState<Page>('dashboard');
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState<any>({
    name: '',
    startDateTime: '',
    endDateTime: '',
    description: '',
    image: ''
  });
  const [addEventError, setAddEventError] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteEventModalOpen, setDeleteEventModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<any>(null);
  const [editEventModalOpen, setEditEventModalOpen] = useState(false);
  const [editEvent, setEditEvent] = useState<any>(null);
  const [deleteMemberModalOpen, setDeleteMemberModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<any>(null);
  const [editMemberModalOpen, setEditMemberModalOpen] = useState(false);
  const [editMember, setEditMember] = useState<any>(null);
  const [eventsPageFilter, setEventsPageFilter] = useState<'All' | 'Scheduled' | 'Completed' | 'Pending'>('All');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [members, setMembers] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const fetchData = async () => {
    try {
      const [membersRes, eventsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/potentialnewmembers`),
        fetch(`${API_BASE_URL}/events`),
      ]);
      if (!membersRes.ok || !eventsRes.ok) throw new Error('Fetch failed');
      const membersData = await membersRes.json();
      const eventsData = await eventsRes.json();
      setMembers(membersData);
      console.log(JSON.stringify(eventsData));
      setEvents(eventsData);
      setDataLoaded(true);

      // Add 3 second delay after data is loaded
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (err) {
      console.error(err);
      setError('Error loading dashboard data.');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileNavClick = (page: Page) => {
    setSelectedPage(page);
    setMobileMenuOpen(false);
  };

  const handleUpcomingEventsClick = () => {
    setEventsPageFilter('Scheduled');
    setSelectedPage('events');
  };

  const handleStatusClick = (status: string) => {
    setEventsPageFilter(status as 'All' | 'Scheduled' | 'Completed' | 'Pending');
    setSelectedPage('events');
  };

  const handleApprovedMembersClick = () => {
    setMembersPageFilter('Approved');
    setSelectedPage('members');
  };

  const handlePendingMembersClick = () => {
    setMembersPageFilter('Pending');
    setSelectedPage('members');
  };

  const handleMemberStatusClick = (status: string) => {
    setMembersPageFilter(status as 'All' | 'Pending' | 'Approved' | 'Rejected');
    setSelectedPage('members');
  };

  const [membersPageFilter, setMembersPageFilter] = useState<'All' | 'Pending' | 'Approved' | 'Rejected'>('All');

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) return (
    <div className="dashboard-loading">
      <img src="/NJPLA_Final_logo-01-scaled.png" alt="NJPLA Logo" className="dashboard-loading-logo" />
      <div className="dashboard-loading-spinner"></div>
      <div className="dashboard-loading-text">Loading Dashboard</div>
      <div className="dashboard-loading-subtitle">Please wait while we fetch your data...</div>
    </div>
  );
  if (error) return <div className="dashboard-error">{error}</div>;

  const filteredMembers = members.filter(m =>
    (m.name?.toLowerCase() || '').includes(memberSearch.toLowerCase()) ||
    (m.email?.toLowerCase() || '').includes(memberSearch.toLowerCase()) ||
    (m.phoneNumber?.toLowerCase() || '').includes(memberSearch.toLowerCase()) ||
    (m.business_Or_Employer?.toLowerCase() || '').includes(memberSearch.toLowerCase())
  );

  const pendingMemberRequests = members.filter(m => (m.status?.toLowerCase() || '') === 'pending').length;
  const upcomingEvents = events.filter(e => (e.status || '') === 'Scheduled').length;
  const totalMembers = members.filter(m => (m.status?.toLowerCase() || '') === 'approved').length;

  const handleEditEvent = (event: any) => {
    setEditEvent(event);
    setEditEventModalOpen(true);
  };

  const handleSaveEvent = async (updatedEvent: any) => {
    try {
      // Call the API to update the event
      const response = await fetch(`${API_BASE_URL}/events/${updatedEvent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedEvent),
      });

      if (!response.ok) {
        throw new Error('Failed to update event');
      }

      // Refresh data after successful update
      await fetchData();
      setEditEventModalOpen(false);
      setEditEvent(null);
    } catch (error) {
      console.error('Error updating event:', error);
      // Handle error appropriately
    }
  };

  const handleDeleteEvent = (event: any) => {
    setEventToDelete(event);
    setDeleteEventModalOpen(true);
  };

  const handleConfirmDelete = async (eventId: number) => {
    try {
      // Call the API to delete the event
      const response = await fetch(`${API_BASE_URL}/events/${eventId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete event');
      }

      // Refresh data after successful deletion
      await fetchData();
      setDeleteEventModalOpen(false);
      setEventToDelete(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      // Handle error appropriately
    }
  };

  const handleEditMember = (member: any) => {
    setEditMember(member);
    setEditMemberModalOpen(true);
  };

  const handleSaveMember = async (updatedMember: any) => {
    try {
      // Call the API to update the member
      const response = await fetch(`${API_BASE_URL}/potentialnewmembers/${updatedMember.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedMember),
      });

      if (!response.ok) {
        throw new Error('Failed to update member');
      }

      // Refresh data after successful update
      await fetchData();
      setEditMemberModalOpen(false);
      setEditMember(null);
    } catch (error) {
      console.error('Error updating member:', error);
      // Handle error appropriately
    }
  };

  const handleDeleteMember = (member: any) => {
    setMemberToDelete(member);
    setDeleteMemberModalOpen(true);
  };

  const handleConfirmDeleteMember = async (memberId: number) => {
    try {
      // Call the API to delete the member
      const response = await fetch(`${API_BASE_URL}/potentialnewmembers/${memberId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete member');
      }

      // Refresh data after successful deletion
      await fetchData();
      setDeleteMemberModalOpen(false);
      setMemberToDelete(null);
    } catch (error) {
      console.error('Error deleting member:', error);
      // Handle error appropriately
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setAddEventError('Please select a valid image file.');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setAddEventError('Image file size must be less than 5MB.');
        return;
      }

      setImageFile(file);
      setAddEventError('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }

    // Clear the input value so the same file can be selected again
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#4f8cff';
    e.currentTarget.style.backgroundColor = '#f8faff';
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#e0e6f0';
    e.currentTarget.style.backgroundColor = '#fff';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#e0e6f0';
    e.currentTarget.style.backgroundColor = '#fff';

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        setAddEventError('Image file size must be less than 5MB.');
        return;
      }

      setImageFile(file);
      setAddEventError('');

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setAddEventError('Please drop a valid image file.');
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setAddEventError('');
  };

  const renderAddEventModal = () => {
    if (!showAddEvent) return null;
    return (
      <div className="dashboard-modal-backdrop">
        <div className="dashboard-modal" style={{ maxWidth: '600px', width: '90vw' }}>
          <h2>Add Event</h2>
          {addEventError && <div className="dashboard-modal-error">{addEventError}</div>}
          <form
            className="dashboard-modal-form"
            onSubmit={async (e) => {
              e.preventDefault();

              if (
                !newEvent.name.trim() ||
                !newEvent.startDateTime.trim() ||
                !newEvent.endDateTime.trim() ||
                !newEvent.description.trim()
              ) {
                setAddEventError("Please fill out all fields.");
                return;
              }

              // Image is now optional - no validation needed here

              try {
                let imageUrl = newEvent.image;

                // If there's an image file, upload it first
                if (imageFile) {
                  const formData = new FormData();
                  formData.append('imageFile', imageFile);

                  const uploadResponse = await fetch(`${API_BASE_URL}/events/upload-image`, {
                    method: 'POST',
                    body: formData
                  });

                  if (!uploadResponse.ok) {
                    const errorData = await uploadResponse.json();
                    console.error('Upload error:', errorData);
                    throw new Error('Failed to upload image');
                  }

                  const uploadResult = await uploadResponse.json();
                  imageUrl = uploadResult.data.url;
                } else {
                  // No image provided, set to empty string
                  imageUrl = '';
                }

                const payload = {
                  Event_Name: newEvent.name,
                  Start_DateTime: new Date(newEvent.startDateTime).toISOString(),
                  End_DateTime: new Date(newEvent.endDateTime).toISOString(),
                  Event_Img: imageUrl,
                  Description: newEvent.description,
                  Submitted_By: `${user?.firstName} ${user?.lastName}`,
                  Submitted_Email: user?.email
                };

                const response = await fetch(`${API_BASE_URL}/events/postScheduledEvent`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json"
                  },
                  body: JSON.stringify(payload)
                });

                if (!response.ok) {
                  const text = await response.text();
                  console.error("Server error:", text);
                  throw new Error("Failed to add event");
                }

                const createdEvent = await response.json();
                setEvents((prev) => [...prev, createdEvent]);
                setShowAddEvent(false);
                setNewEvent({
                  name: '',
                  startDateTime: '',
                  endDateTime: '',
                  description: '',
                  image: ''
                });
                setImageFile(null);
                setImagePreview(null);
                setAddEventError("");
              } catch (err) {
                console.error(err);
                setAddEventError("Error adding event.");
              }
            }}
          >
            <label>
              Name
              <input type="text" value={newEvent.name} onChange={e => setNewEvent((ev: any) => ({ ...ev, name: e.target.value }))} required />
            </label>

            <label>
              Start Date and Time
              <input type="datetime-local" value={newEvent.startDateTime} onChange={e => setNewEvent((ev: any) => ({ ...ev, startDateTime: e.target.value }))} required />
            </label>
            <label>
              End Date and Time
              <input type="datetime-local" value={newEvent.endDateTime} onChange={e => setNewEvent((ev: any) => ({ ...ev, endDateTime: e.target.value }))} required />
            </label>
            <label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span>Description</span>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const textarea = document.querySelector('textarea[name="dashboard-description"]') as HTMLTextAreaElement;
                      if (textarea) {
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const selectedText = textarea.value.substring(start, end);

                        if (start === end) {
                          // No text selected, insert placeholder bold text
                          const newValue = newEvent.description.substring(0, start) + '<strong>Bold Text</strong>' + newEvent.description.substring(end);
                          setNewEvent((ev: any) => ({ ...ev, description: newValue }));
                          setTimeout(() => {
                            textarea.focus();
                            // Position cursor at the end of the bold tag (after </strong>)
                            const boldTagLength = '<strong>Bold Text</strong>'.length;
                            textarea.setSelectionRange(start + boldTagLength, start + boldTagLength);
                          }, 100);
                        } else {
                          // Text selected, wrap it with bold tag
                          const newValue = newEvent.description.substring(0, start) + '<strong>' + selectedText + '</strong>' + newEvent.description.substring(end);
                          setNewEvent((ev: any) => ({ ...ev, description: newValue }));
                          setTimeout(() => {
                            textarea.focus();
                            // Position cursor at the end of the bold tag (after </strong>)
                            const boldTagLength = '<strong>'.length + selectedText.length + '</strong>'.length;
                            textarea.setSelectionRange(start + boldTagLength, start + boldTagLength);
                          }, 100);
                        }
                      }
                    }}
                    style={{
                      background: '#ffe6e6',
                      color: '#d32f2f',
                      border: '1px solid #d32f2f',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#ffcccc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#ffe6e6';
                    }}
                    title="Make selected text bold"
                  >
                    <strong>B</strong> Bold Text
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const textarea = document.querySelector('textarea[name="dashboard-description"]') as HTMLTextAreaElement;
                      if (textarea) {
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const currentValue = newEvent.description;
                        const newValue = currentValue.substring(0, start) + '</br>' + currentValue.substring(end);
                        setNewEvent((ev: any) => ({ ...ev, description: newValue }));

                        // Set cursor position after the inserted tag
                        setTimeout(() => {
                          textarea.focus();
                          textarea.setSelectionRange(start + 5, start + 5);
                        }, 100);
                      }
                    }}
                    style={{
                      background: '#f0f4ff',
                      color: '#3366ff',
                      border: '1px solid #3366ff',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e6f0ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f0f4ff';
                    }}
                    title="+ Add Break to description"
                  >
                  + Line Break
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const textarea = document.querySelector('textarea[name="dashboard-description"]') as HTMLTextAreaElement;
                      if (textarea) {
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const selectedText = textarea.value.substring(start, end);

                        if (start === end) {
                          // No text selected, insert placeholder link
                          const newValue = newEvent.description.substring(0, start) + '<a href="https://example.com">Link Text</a>' + newEvent.description.substring(end);
                          setNewEvent((ev: any) => ({ ...ev, description: newValue }));
                          setTimeout(() => {
                            textarea.focus();
                            // Position cursor at the end of the link tag (after </a>)
                            const linkTagLength = '<a href="https://example.com">Link Text</a>'.length;
                            textarea.setSelectionRange(start + linkTagLength, start + linkTagLength);
                          }, 100);
                        } else {
                          // Text selected, wrap it with link tag
                          const newValue = newEvent.description.substring(0, start) + '<a href="https://example.com">' + selectedText + '</a>' + newEvent.description.substring(end);
                          setNewEvent((ev: any) => ({ ...ev, description: newValue }));
                          setTimeout(() => {
                            textarea.focus();
                            // Position cursor at the end of the link tag (after </a>)
                            const linkTagLength = '<a href="https://example.com">'.length + selectedText.length + '</a>'.length;
                            textarea.setSelectionRange(start + linkTagLength, start + linkTagLength);
                          }, 100);
                        }
                      }
                    }}
                    style={{
                      background: '#fffbf0',
                      color: '#b8860b',
                      border: '1px solid #b8860b',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#fff8e6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#fffbf0';
                    }}
                    title="Make selected text a hyperlink"
                  >
                    🔗 Make Link
                  </button>
                </div>
              </div>
              <textarea
                name="dashboard-description"
                value={newEvent.description}
                onChange={e => setNewEvent((ev: any) => ({ ...ev, description: e.target.value }))}
                required
                placeholder="Enter event description..."
                style={{ minHeight: '80px', width: '90%' }}
              />
            </label>
            <label>
              Event Image (Optional)
              <div
                style={{
                  border: '2px dashed #e0e6f0',
                  borderRadius: '8px',
                  padding: '20px',
                  textAlign: 'center',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  margin: '0 8px'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <div style={{ position: 'relative' }}>
                    <img
                      src={imagePreview}
                      alt="Event preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        borderRadius: '4px',
                        border: '1px solid #e0e6f0'
                      }}
                    />
                    {imageFile && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage();
                        }}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: '#f36b6b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Remove image"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '24px', marginBottom: '8px', color: '#666' }}>📷</div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                      Click to upload or drag and drop
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      PNG, JPG, GIF up to 5MB
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
              </div>
            </label>
            <div className="dashboard-modal-actions">
              <button type="button" className="dashboard-modal-cancel" onClick={() => {
                setShowAddEvent(false);
                setAddEventError('');
                setNewEvent({
                  name: '',
                  startDateTime: '',
                  endDateTime: '',
                  description: '',
                  image: ''
                });
                setImageFile(null);
                setImagePreview(null);
              }}>Cancel</button>
              <button type="submit" className="dashboard-modal-save">Save</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  function renderPage() {
    switch (selectedPage) {
      case 'dashboard':
        return (
          <>
            <div className="dashboard-stats">
              <div className="dashboard-stat" style={{ cursor: 'pointer' }} onClick={handleUpcomingEventsClick}>
                <span className="dashboard-stat-svg">
                  <svg width="28" height="28" fill="none" viewBox="0 0 28 28"><g><circle cx="14" cy="14" r="14" fill="#f4f8ff" /><rect x="7" y="10" width="14" height="8" rx="2" fill="#3366ff" fillOpacity=".15" /><rect x="7" y="10" width="14" height="8" rx="2" stroke="#3366ff" strokeWidth="1.5" /><rect x="10" y="7" width="1.5" height="4" rx=".75" fill="#3366ff" /><rect x="16.5" y="7" width="1.5" height="4" rx=".75" fill="#3366ff" /></g></svg>
                </span>
                <div className="dashboard-stat-value">{upcomingEvents}</div>
                <div className="dashboard-stat-label">Scheduled Events</div>
              </div>
              <div className="dashboard-stat" style={{ cursor: 'pointer' }} onClick={handleApprovedMembersClick}>
                <span className="dashboard-stat-svg">
                  <svg width="28" height="28" fill="none" viewBox="0 0 28 28"><g><circle cx="14" cy="14" r="14" fill="#f4f8ff" /><path d="M10.5 13.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm7 0a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm-7 1.5c-1.67 0-5 0.84-5 2.5V20a1 1 0 0 0 1 1h6.5v-2.5c0-.83.67-1.5 1.5-1.5h2c.83 0 1.5.67 1.5 1.5V21H23a1 1 0 0 0 1-1v-2.5c0-1.66-3.33-2.5-5-2.5h-7Z" fill="#3366ff" /></g></svg>
                </span>
                <div className="dashboard-stat-value">{totalMembers.toLocaleString()}</div>
                <div className="dashboard-stat-label">Approved Members</div>
              </div>
              <div className="dashboard-stat" style={{ cursor: 'pointer' }} onClick={handlePendingMembersClick}>
                <span className="dashboard-stat-svg">
                  <svg width="28" height="28" fill="none" viewBox="0 0 28 28"><g><circle cx="14" cy="14" r="14" fill="#f4f8ff" /><path d="M14 14.5a3.25 3.25 0 1 0 0-6.5 3.25 3.25 0 0 0 0 6.5Zm0 1.5c-2.17 0-6.5 1.09-6.5 3.25V21a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-1.75c0-2.16-4.33-3.25-6.5-3.25Z" fill="#3366ff" /></g></svg>
                </span>
                <div className="dashboard-stat-value">{pendingMemberRequests}</div>
                <div className="dashboard-stat-label">Pending Member Requests</div>
              </div>
            </div>

            <section className="dashboard-section">
              <div className="dashboard-section-header" style={{ gap: 8 }}>
                <h2 style={{ fontWeight: 700 }}>Events</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="dashboard-add-btn" style={{ fontSize: '0.92rem', fontWeight: 700, padding: '8px 18px' }} onClick={() => setShowAddEvent(true)}>Add Event</button>
                  <button className="dashboard-add-btn" style={{ background: '#fff', color: '#3366ff', border: '1.5px solid #3366ff', fontWeight: 600, fontSize: '0.92rem', padding: '8px 18px' }} onClick={() => {
                    setEventsPageFilter('All');
                    setSelectedPage('events');
                  }}>View All</button>
                </div>
              </div>
              <div className="dashboard-table-wrapper members-table-scroll">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Event Name</th>
                      <th>Start Date/Time</th>
                      <th>End Date/Time</th>
                      <th>Description</th>
                      <th>Image</th>
                      <th>Submitted</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.length === 0 ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', fontSize: '0.9rem', padding: '40px 20px' }}>No events found. Click "Add Event" to create your first event.</td></tr>
                    ) : (
                      events.map((e, i) => (
                        console.log(e),
                        <tr key={e.id}>
                          <td style={{ fontSize: '0.9rem' }}>{e.event_Name || 'No name'}</td>
                          <td style={{ fontSize: '0.9rem' }}>
                            {e.start_Datetime ? (
                              <div>
                                <div style={{ fontSize: '0.9rem' }}>{new Date(e.start_Datetime).toLocaleDateString()}</div>
                                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                  {new Date(e.start_Datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            ) : 'No start time'}
                          </td>
                          <td style={{ fontSize: '0.9rem' }}>
                            {e.end_Datetime ? (
                              <div>
                                <div style={{ fontSize: '0.9rem' }}>{new Date(e.end_Datetime).toLocaleDateString()}</div>
                                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                  {new Date(e.end_Datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            ) : 'No end time'}
                          </td>
                          <td style={{ fontSize: '0.9rem' }}>{renderHtmlContent(e.description || '')}</td>
                          <td style={{ fontSize: '0.9rem' }}>
                            {e.event_Img ? (
                              <img
                                src={e.event_Img}
                                alt={e.event_Name}
                                style={{
                                  width: '40px',
                                  height: '30px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  border: '1px solid #e0e6f0',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                }}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                                onClick={() => handleEditEvent(e)}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                                title="Click to edit event"
                              />
                            ) : (
                              <span style={{ color: '#888', fontSize: '0.9rem' }}>No image</span>
                            )}
                          </td>
                          <td style={{ fontSize: '0.9rem' }}>
                            {e.submitted_By ? (
                              <div>
                                <div className="submitted-info-desktop">
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>{e.submitted_By}</span>
                                    {e.created_At && (
                                      <span style={{ fontSize: '0.9rem', color: '#666' }}>{new Date(e.created_At).toLocaleDateString()} {new Date(e.created_At).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    )}
                                  </div>
                                  {e.submitted_Email && (
                                    <div style={{ fontSize: '0.9rem', color: '#666' }}>{e.submitted_Email}</div>
                                  )}
                                </div>
                                <div className="submitted-info-mobile">
                                  <div style={{ fontSize: '0.9rem' }}>{e.submitted_By}</div>
                                  {e.submitted_Email && (
                                    <div style={{ fontSize: '0.9rem', color: '#666' }}>{e.submitted_Email}</div>
                                  )}
                                  {e.created_At && (
                                    <div style={{ fontSize: '0.9rem', color: '#666' }}>{new Date(e.created_At).toLocaleDateString()} {new Date(e.created_At).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span style={{ color: '#888', fontSize: '0.9rem' }}>Admin</span>
                            )}
                          </td>
                          <td>
                            <span
                              className={`dashboard-badge dashboard-badge-${(e.status?.toLowerCase() || 'unknown')}`}
                              style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                              onClick={() => handleStatusClick(e.status || 'Unknown')}
                              title={`Click to view all ${e.status || 'Unknown'} events`}
                            >
                              {e.status || 'Unknown'}
                            </span>
                          </td>
                          <td>
                            <button className="dashboard-icon-btn edit" title="Edit" aria-label="Edit" onClick={() => handleEditEvent(e)}>
                              <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                                <circle cx="10" cy="10" r="10" fill="#eaf3ff" />
                                <path d="M13 7l-6 6v2h2l6-6-2-2z" stroke="#4f8cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                <path d="M11 5l2 2" stroke="#4f8cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                            <button className="dashboard-icon-btn delete" title="Delete" aria-label="Delete" onClick={() => handleDeleteEvent(e)}>
                              <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                                <circle cx="10" cy="10" r="10" fill="#fff0f0" />
                                <path d="M6.5 6.5l7 7m0-7l-7 7" stroke="#f36b6b" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="dashboard-section">
              <div className="dashboard-section-header" style={{ gap: 8 }}>
                <h2 style={{ fontWeight: 700 }}>Potential New Members</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: "center" }}>
                  <input
                    className="dashboard-search"
                    placeholder="Search members..."
                    value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                  />
                  <button className="dashboard-add-btn" style={{ background: '#fff', color: '#3366ff', border: '1.5px solid #3366ff', fontWeight: 600, fontSize: '0.92rem', padding: '8px 18px' }} onClick={() => {
                    setMembersPageFilter('All');
                    setSelectedPage('members');
                  }}>View All</button>
                </div>
              </div>
              <div className="dashboard-table-wrapper members-table-scroll">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Business/Employer</th>
                      <th>Applied On</th>
                      <th>Status</th>
                      <th colSpan={2}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length === 0 ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', fontSize: '0.9rem', padding: '40px 20px' }}>No members found. {memberSearch ? 'Try adjusting your search terms.' : 'No potential new members have applied yet.'}</td></tr>
                    ) : (
                      filteredMembers.map((m, i) => (
                        <tr key={m.id}>
                          <td style={{ fontSize: '0.9rem' }}>{m.name || 'No name'}</td>
                          <td style={{ fontSize: '0.9rem' }}>{m.email || 'No email'}</td>
                          <td style={{ fontSize: '0.9rem' }}>{m.phoneNumber || 'No phone'}</td>
                          <td style={{ fontSize: '0.9rem' }}>{m.business_Or_Employer || 'No business'}</td>
                          <td style={{ fontSize: '0.9rem' }}>{m.applied_On ? new Date(m.applied_On).toLocaleDateString() : 'No date'}</td>
                          <td>
                            <span
                              className={`dashboard-badge ${(m.status?.toLowerCase() || '') === 'pending' ? 'dashboard-badge-pending' :
                                (m.status?.toLowerCase() || '') === 'approved' ? 'dashboard-badge-approved' :
                                  (m.status?.toLowerCase() || '') === 'rejected' ? 'dashboard-badge-rejected' : ''
                                }`}
                              style={{ fontSize: '0.9rem', cursor: 'pointer' }}
                              onClick={() => handleMemberStatusClick(m.status || 'Unknown')}
                              title={`Click to view all ${m.status || 'Unknown'} members`}
                            >
                              {m.status || 'Unknown'}
                            </span>
                          </td>
                          <td>
                            <button className="dashboard-icon-btn edit" title="Edit" aria-label="Edit" onClick={() => handleEditMember(m)}>
                              <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                                <circle cx="10" cy="10" r="10" fill="#eaf3ff" />
                                <path d="M13 7l-6 6v2h2l6-6-2-2z" stroke="#4f8cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                <path d="M11 5l2 2" stroke="#4f8cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                            <button className="dashboard-icon-btn delete" title="Delete" aria-label="Delete" onClick={() => handleDeleteMember(m)}>
                              <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                                <circle cx="10" cy="10" r="10" fill="#fff0f0" />
                                <path d="M6.5 6.5l7 7m0-7l-7 7" stroke="#f36b6b" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
            {renderAddEventModal()}
            <EditEventModal
              event={editEvent}
              isOpen={editEventModalOpen}
              onClose={() => {
                setEditEventModalOpen(false);
                setEditEvent(null);
              }}
              onSave={handleSaveEvent}
            />
            <DeleteEventModal
              event={eventToDelete}
              isOpen={deleteEventModalOpen}
              onClose={() => {
                setDeleteEventModalOpen(false);
                setEventToDelete(null);
              }}
              onConfirm={handleConfirmDelete}
            />
            <EditMemberModal
              member={editMember}
              isOpen={editMemberModalOpen}
              onClose={() => {
                setEditMemberModalOpen(false);
                setEditMember(null);
              }}
              onSave={handleSaveMember}
            />
            <DeleteMemberModal
              member={memberToDelete}
              isOpen={deleteMemberModalOpen}
              onClose={() => {
                setDeleteMemberModalOpen(false);
                setMemberToDelete(null);
              }}
              onConfirm={handleConfirmDeleteMember}
            />
          </>
        );
      case 'events':
        return <EventsPage events={events} onEdit={(i) => {
          // Just refresh the data when child page makes changes
          fetchData();
        }} onDelete={(i) => {
          // Just refresh the data when child page makes changes
          fetchData();
        }}
          onAddEvent={(event) => setEvents(prev => [...prev, event])}
          defaultFilter={eventsPageFilter}
        />;

      case 'members':
        return <MembersPage
          members={members}
          searchQuery={memberSearch}
          onSearchChange={setMemberSearch}
          onEdit={(i) => {
            // Just refresh the data when child page makes changes
            fetchData();
          }}
          onDelete={(i) => {
            // Just refresh the data when child page makes changes
            fetchData();
          }}
          defaultFilter={membersPageFilter}
        />;

      default:
        return null;
    }
  }

  return (
    <div className="dashboard-root">
      {/* Sticky Top Bar */}
      <div className="dashboard-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            className="dashboard-mobile-menu-toggle"
            onClick={handleMobileMenuToggle}
            aria-label="Toggle mobile menu"
          >
            ☰
          </button>
          <img
            src="/NJPLA_Final_logo-01-scaled.png"
            alt="NJPLA Logo"
            className="dashboard-topbar-logo"
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedPage('dashboard')}
            title="Click to return to dashboard"
          />
        </div>
        <div className="dashboard-topbar-user">
          <span className="dashboard-topbar-avatar">{user?.firstName?.[0]?.toUpperCase() || 'A'}{user?.lastName?.[0]?.toUpperCase() || 'A'}</span>
          <span className="dashboard-topbar-name">{user?.firstName + " " + user?.lastName}</span>
        </div>
      </div>

      {/* Main Layout */}
      <div className="dashboard-main-layout">
        {/* Sidebar with Navigation */}
        <aside className="dashboard-sidebar">
          <nav className="dashboard-nav">
            <button className={`dashboard-nav-link${selectedPage === 'dashboard' ? ' active' : ''}`} onClick={() => setSelectedPage('dashboard')}>
              Dashboard
            </button>
            <button className={`dashboard-nav-link${selectedPage === 'events' ? ' active' : ''}`} onClick={() => {
              setEventsPageFilter('All');
              setSelectedPage('events');
            }}>
              Events
            </button>
            <button className={`dashboard-nav-link${selectedPage === 'members' ? ' active' : ''}`} onClick={() => {
              setMembersPageFilter('All');
              setSelectedPage('members');
            }}>
              Members
            </button>

            <button className="dashboard-nav-link dashboard-logout" onClick={handleLogout}>
              Logout
            </button>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="dashboard-main">
          <div className="dashboard-content">{renderPage()}</div>
          <footer className="dashboard-footer">
            Powered by <a href="https://www.enterprisewebsolutions.co" target="_blank" rel="noopener noreferrer">Enterprise Web Solutions</a>
          </footer>
        </main>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="dashboard-mobile-menu-overlay" onClick={handleMobileMenuClose} />
      )}

      {/* Mobile Menu */}
      <div className={`dashboard-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="dashboard-mobile-menu-header">
          <img
            src="/NJPLA_Final_logo-01-scaled.png"
            alt="NJPLA Logo"
            style={{ height: '40px', width: 'auto' }}
          />
          <button
            className="dashboard-mobile-menu-close"
            onClick={handleMobileMenuClose}
            aria-label="Close mobile menu"
          >
            ×
          </button>
        </div>
        <nav className="dashboard-mobile-nav">
          <button
            className={`dashboard-mobile-nav-link${selectedPage === 'dashboard' ? ' active' : ''}`}
            onClick={() => handleMobileNavClick('dashboard')}
          >
            Dashboard
          </button>
          <button
            className={`dashboard-mobile-nav-link${selectedPage === 'events' ? ' active' : ''}`}
            onClick={() => {
              setEventsPageFilter('All');
              handleMobileNavClick('events');
            }}
          >
            Events
          </button>
          <button
            className={`dashboard-mobile-nav-link${selectedPage === 'members' ? ' active' : ''}`}
            onClick={() => {
              setMembersPageFilter('All');
              handleMobileNavClick('members');
            }}
          >
            Members
          </button>

          <button
            className="dashboard-mobile-nav-link dashboard-logout"
            onClick={handleLogout}
          >
            Logout
          </button>
        </nav>
      </div>
    </div>
  );
};

export default DashboardPage;